/**
 * @ngdoc service
 * @name flowApiService
 * @module flowingly.runner.services
 *
 * @description A service responsible solely for fetching flow data
 *
 * ## Notes
 * No Caching or formatting to be performed here.
 *
 */
import angular from 'angular';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { HttpStatusCodes } from '@Shared.Angular/@types/core/contracts/constants/httpStatusCodes';
import { FieldFiles } from '@Shared.FormGen/angular-form-gen/form/files/file.listService';
import IFormInput from '@Shared.Angular/@types/core/contracts/queryModel/card/formInput';
import IStepReassignmentDto from '@Shared.Angular/@types/core/contracts/queryModel/delegation/stepReassignmentDto';
import { IFlowForUserPascalCase } from '@Shared.Angular/@types/core/contracts/queryModel/flows/flowForUser';
import IFlowStepAssistantResponse from '@Shared.Angular/@types/core/contracts/queryModel/flows/flowStepAssistantResponse';
import { IGroupedFlowsForUser } from '@Shared.Angular/@types/core/contracts/queryModel/flows/groupedFlowsForUser';
import { Guid } from '@Shared.Angular/@types/guid';
import INodeCustomEmailCommandData from '@Shared.Angular/@types/core/contracts/commands/modeler/nodeCustomEmailCommandData';
import IStepNudgeHistoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/flows/stepNudgeHistory';
import { IDynamicUserQueryModel } from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/dynamicUserQueryModel';

export interface IApproversAndDynamicActorsResponse {
  multipleApprovers: unknown[];
  dynamicActors: Array<{
    dynamicActors: unknown[];
    nodeId: Guid | null;
    stepName: string;
  }>;
}

export interface IWebhookOverride {
  stepId: string;
  flowId: string;
  userId: string;
  reason: string;
}

interface IStepFormSaveRequest {
  flowId: Guid;
  stepId: Guid;
  cardData: string;
}

interface IQueuedStepFormSave {
  request: IStepFormSaveRequest;
  deferreds: angular.IDeferred<void>[];
}

export default class FlowApiService {
  public static $inject = [
    '$q',
    '$http',
    'APP_CONFIG',
    'sessionService',
    'runnerCardService'
  ];

  constructor(
    private $q: angular.IQService,
    private $http: angular.IHttpService,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private sessionService: SharedAngular.SessionService,
    private runnerCardService: RunnerCardService
  ) {}

  public completeTask(
    stepId: Guid,
    flowId: Guid,
    formData: IFormInput[],
    files: FieldFiles[],
    assignedDynamicActors,
    selectedApprovers
  ) {
    const cardData = JSON.stringify(
      this.runnerCardService.addFileIdsToForm(formData, files)
    );
    return this.$http
      .post(`${this.APP_CONFIG.apiBaseUrl}flows/${flowId}/steps/${stepId}`, {
        UserId: this.getUserId(),
        FlowInstanceId: flowId,
        StepId: stepId,
        cardData: cardData,
        isNewFlow: false,
        assignedDynamicActors: assignedDynamicActors,
        selectedApprovers: selectedApprovers
      })
      .then((response) => {
        return response;
      });
  }

  public nudgeFlowWaitingOnActor(stepNudgeHistory: IStepNudgeHistoryDetail) {
    return this.$http.post<void>(
      this.APP_CONFIG.apiBaseUrl + 'nudge/step/',
      stepNudgeHistory
    );
  }

  public getStepNudgeHistoryList(noSpinner, stepId) {
    return this.$http
      .get<IStepNudgeHistoryDetail[]>(
        `${this.APP_CONFIG.apiBaseUrl}nudge/step/${stepId}`,
        { noSpinner: noSpinner }
      )
      .then((response) => {
        return response.data;
      });
  }

  public getFlowById(flowId: Guid, noSpinner: boolean) {
    return this.$http
      .get<IFlowForUserPascalCase>(
        this.APP_CONFIG.apiBaseUrl + 'flows/' + flowId,
        { noSpinner: noSpinner }
      )
      .then((response) => {
        return response.data;
      });
  }

  public generateFlowStepAssistant(
    flowId: Guid,
    stepId: Guid
  ): angular.IPromise<IFlowStepAssistantResponse> {
    return this.$http
      .post<IFlowStepAssistantResponse>(
        `${this.APP_CONFIG.apiBaseUrl}flows/${flowId}/steps/${stepId}/assistant`,
        {},
        { noSpinner: true }
      )
      .then((response) => {
        return response.data;
      });
  }

  public getFlowStepAssistant(
    flowId: Guid,
    stepId: Guid
  ): angular.IPromise<IFlowStepAssistantResponse | null> {
    return this.$http
      .get<IFlowStepAssistantResponse>(
        `${this.APP_CONFIG.apiBaseUrl}flows/${flowId}/steps/${stepId}/assistant`,
        { noSpinner: true }
      )
      .then((response) => {
        if (response.status === HttpStatusCodes.NoContent) {
          return null;
        }

        return response.data;
      });
  }

  public getApproversAndDynamicActorsForNodes(
    options: IDynamicUserQueryModel
  ): angular.IPromise<IApproversAndDynamicActorsResponse> {
    return this.$http
      .post<unknown>(
        this.APP_CONFIG.apiBaseUrl + 'flows/approversAndDynamicActorsForNodes',
        options,
        {
          noSpinner: true
        }
      )
      .then((response) => {
        const responseData = response.data;
        return (
          typeof responseData === 'string'
            ? JSON.parse(responseData)
            : responseData
        ) as IApproversAndDynamicActorsResponse;
      });
  }

  public getFlowsImIn(
    onlyStartedByMe: boolean,
    startDateUtc: string,
    endDateUtc: string
  ) {
    return this.$http
      .get<IGroupedFlowsForUser[]>(
        `${this.APP_CONFIG.apiBaseUrl}flows/v2/` +
          `?startedByMeOnly=${onlyStartedByMe}` +
          `&startDate=${startDateUtc}` +
          `&endDate=${endDateUtc}`,

        { noSpinner: false }
      )
      .then((response) => {
        return response.data;
      });
  }

  public getFlowsTodo() {
    return this.$http
      .get<IGroupedFlowsForUser[]>(
        this.APP_CONFIG.apiBaseUrl + 'flows/todov2',
        {
          noSpinner: false
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  public getFlowSearchResults(searchTerm: string) {
    const userId = this.getUserId();
    const encodedTerm = encodeURIComponent(searchTerm);
    return this.$http
      .get<IGroupedFlowsForUser[]>(
        this.APP_CONFIG.apiBaseUrl +
          `flows/searchV2?userId=${userId}&term=${encodedTerm}`,
        { noSpinner: false }
      )
      .then((response) => {
        return response.data;
      });
  }

  private readonly stepIdsWithInFlightFormSaveRequests = new Set<Guid>();
  private readonly queuedStepFormSaves = new Map<Guid, IQueuedStepFormSave>();
  public saveFormProgress(
    flowId: Guid,
    stepId: Guid,
    formData,
    files: FieldFiles[]
  ) {
    const request: IStepFormSaveRequest = {
      flowId: flowId,
      stepId: stepId,
      cardData: JSON.stringify(
        this.runnerCardService.addFileIdsToForm(formData, files)
      )
    };

    if (this.stepIdsWithInFlightFormSaveRequests.has(stepId)) {
      const deferred = this.$q.defer<void>();
      const queuedSave = this.queuedStepFormSaves.get(stepId);

      if (queuedSave) {
        queuedSave.request = request;
        queuedSave.deferreds.push(deferred);
      } else {
        this.queuedStepFormSaves.set(stepId, {
          request: request,
          deferreds: [deferred]
        });
      }

      return deferred.promise;
    }

    return this.sendFormProgressSave(request);
  }

  private sendFormProgressSave(request: IStepFormSaveRequest) {
    this.stepIdsWithInFlightFormSaveRequests.add(request.stepId);
    return this.$http
      .post<void>(this.APP_CONFIG.apiBaseUrl + 'flows/saveStepProgress', {
        StepId: request.stepId,
        UserId: this.getUserId(),
        FlowInstanceId: request.flowId,
        cardData: request.cardData
      })
      .finally(() => {
        this.stepIdsWithInFlightFormSaveRequests.delete(request.stepId);

        const queuedSave = this.queuedStepFormSaves.get(request.stepId);
        this.queuedStepFormSaves.delete(request.stepId);

        if (!queuedSave) {
          return;
        }

        this.sendFormProgressSave(queuedSave.request).then(
          () => queuedSave.deferreds.forEach((deferred) => deferred.resolve()),
          (error) =>
            queuedSave.deferreds.forEach((deferred) => deferred.reject(error))
        );
      });
  }

  public startFlow(flowId, subject, assignedActorId, ccActors) {
    //start a flow with specified flow id and the subject user has entered
    return this.$http
      .post<Guid[]>(this.APP_CONFIG.apiBaseUrl + 'flows', {
        UserId: this.getUserId(),
        FlowId: flowId,
        Subject: subject,
        AssignedActorId: assignedActorId,
        CCActors: ccActors
      })
      .then((response) => {
        return response.data;
      });
  }

  public bulkStartFlow(
    flowModelId,
    subject,
    actors,
    ccActors,
    assignedActorId,
    shouldAppendNameToSubject?: boolean,
    excludeActors?: Guid[]
  ) {
    const url = this.APP_CONFIG.apiBaseUrl + 'flows/bulkStart';
    const data = {
      FlowModelId: flowModelId,
      Subject: subject,
      ActorsToStartFlowFor: actors,
      CCActors: ccActors,
      AssignedActorId: assignedActorId,
      ShouldAppendNameToSubject: shouldAppendNameToSubject,
      ExcludeActors: excludeActors
    };

    return this.$http.post<Guid[]>(url, data).then((response) => {
      return response.data;
    });
  }

  public withdrawFlow(data): angular.IPromise<boolean> {
    const request = {
      FlowInstanceId: data.flowId,
      CancellationComment: data.comment,
      EntitiesToNotify: data.selectedActors
    };

    return this.$http
      .post<boolean>(`${this.APP_CONFIG.apiBaseUrl}flows/withdraw`, request)
      .then((response) => response.data);
  }

  public deleteFlow(flowId: Guid) {
    const url = `${this.APP_CONFIG.apiBaseUrl}flows/${flowId}`;

    return this.$http.delete<void>(url).then((response) => response);
  }

  public reassignStep(data: IStepReassignmentDto) {
    return this.$http.post<void>(
      this.APP_CONFIG.apiBaseUrl + 'delegation/reassignTask',
      data
    );
  }

  private getUserId() {
    return this.sessionService.getUser().id;
  }

  public getFlowSupportDetails(flowId) {
    return this.$http
      .get<string>(this.APP_CONFIG.apiBaseUrl + 'flows/support/' + flowId)
      .then((response) => {
        return response.data;
      });
  }

  public addStepTask(data) {
    return this.$http
      .post(this.APP_CONFIG.apiBaseUrl + 'flows/addStepTask', data)
      .then((response) => {
        return response;
      });
  }

  public updateStepTaskStatus(stepTaskId, status) {
    return this.$http
      .put(
        this.APP_CONFIG.apiBaseUrl +
          'flows/steptask/' +
          stepTaskId +
          '/status/' +
          status,
        undefined
      )
      .then((response) => {
        return response;
      });
  }

  public updateStepTaskApproval(stepTaskId, approverUserId, approved, comment) {
    const request = {
      stepTaskId: stepTaskId,
      approverUserId: approverUserId,
      approved: approved,
      comment: comment
    };

    return this.$http
      .post(this.APP_CONFIG.apiBaseUrl + 'flows/steptask/approval', request)
      .then((response) => {
        return response;
      });
  }

  public cancelStepTask(data) {
    const request = {
      UserId: this.getUserId(),
      StepTaskId: data.stepTaskId,
      CancellationComment: data.comment,
      EntitiesToNotify: data.selectedActors
    };

    return this.$http
      .post(this.APP_CONFIG.apiBaseUrl + 'flows/steptask/cancel', request)
      .then(
        () => true,
        () => false
      );
  }

  public getStepTask(stepTaskId) {
    return this.$http
      .get(this.APP_CONFIG.apiBaseUrl + 'flows/steptask/' + stepTaskId)
      .then((response) => {
        return response.data;
      });
  }

  public overrideProcessingIntegration(data: IWebhookOverride) {
    return this.$http
      .post(this.APP_CONFIG.apiBaseUrl + 'flows/overrideIntegrationState', data)
      .then((response) => {
        return response.data;
      });
  }

  public resendExternalUserEmail(
    nodeCustomEmail: INodeCustomEmailCommandData,
    stepId: Guid,
    flowId: Guid
  ) {
    return this.$http
      .post<void>(
        `${this.APP_CONFIG.apiBaseUrl}flows/${flowId}/steps/${stepId}/emails`,
        nodeCustomEmail
      )
      .then((response) => {
        return response.data;
      });
  }
}

angular
  .module('flowingly.runner.services')
  .service('flowApiService', FlowApiService);

export type FlowApiServiceType = InstanceType<typeof FlowApiService>;
